import React, { useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
} from "@chakra-ui/react";
import { FiCheckSquare, FiBarChart2, FiZap, FiClock, FiCpu } from "react-icons/fi";
import { getCurrentDate, getFirstDayOfMonth } from "Utils/dateUtils";
import { useDateFilter } from "Hooks/useDateFilter";
import { SCREEN_KEYS } from "constants/screenKeys";
import DateRangeFilter from "components/DateRangeFilter/DateRangeFilter";
import AvailabilityAiVsManualDp from "./AvailabilityAiVsManualDp";
import ActivationManualOutperformsAiDp from "./ActivationManualOutperformsAiDp";
import ActivationAiOutperformsManualDp from "./ActivationAiOutperformsManualDp";
import CoolerPurtiyDp from "./CoolerPurtiyDp";

function VarianceReportAiVsManual() {
  const { fromDate, toDate, setFromDate, setToDate } = useDateFilter(
    SCREEN_KEYS.VARIANCE_REPORT_AI_VS_MANUAL,
    { fromDate: getFirstDayOfMonth(), toDate: getCurrentDate() }
  );

  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (value > toDate) setToDate(value);
  };

  const handleToDateChange = (value) => {
    if (value < fromDate) return;
    setToDate(value);
  };

  const [activationSubTab, setActivationSubTab] = useState(0);
  const [coolerPuritySubTab, setCoolerPuritySubTab] = useState(0);
  const [manualOutperformsTotal, setManualOutperformsTotal] = useState(0);
  const [aiOutperformsTotal, setAiOutperformsTotal] = useState(0);
  const activationTotal = activationSubTab === 0 ? manualOutperformsTotal : aiOutperformsTotal;

  const tabStyle = {
    fontWeight: "semibold",
    fontSize: "md",
    py: 3,
    px: 6,
    borderRadius: "lg",
    color: "gray.600",
    display: "flex",
    gap: 2,
    alignItems: "center",
    _selected: { bg: "red.600", color: "white", boxShadow: "md" },
    _hover: { bg: "red.200", color: "red.700" },
  };

  return (
    <Box minH="100vh" bg="gray.50" pt={{ base: "10vh", md: "13vh" }} px={6}>
      {/* Page Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        mb={6}
      >
        <Box>
          <Heading size="lg" color="gray.800" mb={2}>
            Variance Report Ai Vs Manual
          </Heading>
          <Text color="gray.500">
            Compare AI-detected availability, activation, and cooler purity against manual audits
          </Text>
        </Box>
      </Flex>

      {/* Main Tabs */}
      <Tabs variant="unstyled" isLazy>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          mb={6}
          gap={4}
        >
          <TabList
            bg="red.100"
            p={2}
            borderRadius="lg"
            gap={2}
            display="inline-flex"
          >
            <Tab {...tabStyle}>
              <FiCheckSquare size={18} /> Availability
            </Tab>
            <Tab {...tabStyle}>
              <FiZap size={18} /> Activation
            </Tab>
            <Tab {...tabStyle}>
              <FiBarChart2 size={18} /> Cooler Purity
            </Tab>
          </TabList>

          <DateRangeFilter
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
          />
        </Flex>

        <TabPanels>
          {/* ── Tab 1: Availability ── */}
          <TabPanel p={0}>
            <AvailabilityAiVsManualDp fromDate={fromDate} toDate={toDate} />
          </TabPanel>

          {/* ── Tab 2: Activation ── */}
          <TabPanel p={0}>
            <Tabs
              variant="unstyled"
              isLazy
              index={activationSubTab}
              onChange={setActivationSubTab}
            >
              <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "flex-start", md: "center" }}
                mb={6}
                gap={4}
              >
                <TabList
                  bg="red.50"
                  p={2}
                  borderRadius="lg"
                  gap={2}
                  display="inline-flex"
                >
                  <Tab {...tabStyle}>
                    <FiClock size={18} /> Manual {`>`} AI
                  </Tab>
                  <Tab {...tabStyle}>
                    <FiCpu size={18} /> AI {`>`}Manual
                  </Tab>
                </TabList>

                <Flex
                  align="center" gap={1.5} bg="gray.700"
                  px={4} py={1.5} borderRadius="full"
                  boxShadow="0 2px 4px rgba(0,0,0,0.2)"
                >
                  <Text fontSize="sm" fontWeight="bold" color="white">Total:</Text>
                  <Text color="white" fontSize="md" fontWeight="bold">{activationTotal} Records</Text>
                </Flex>
              </Flex>

              <TabPanels>
                <TabPanel p={0}>
                  <ActivationManualOutperformsAiDp
                    fromDate={fromDate}
                    toDate={toDate}
                    onTotalElementsChange={setManualOutperformsTotal}
                  />
                </TabPanel>
                <TabPanel p={0}>
                  <ActivationAiOutperformsManualDp
                    fromDate={fromDate}
                    toDate={toDate}
                    onTotalElementsChange={setAiOutperformsTotal}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </TabPanel>
          

          {/* ── Tab 3: Cooler Purity ── */}
          <TabPanel p={0} >
            <Tabs
              variant="unstyled"
              isLazy
              index={coolerPuritySubTab}
              onChange={setCoolerPuritySubTab}
            >
            <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "flex-start", md: "center" }}
                mb={6}
                gap={4}
              >
               <TabList
                  bg="red.50"
                  p={2}
                  borderRadius="lg"
                  gap={2}
                  display="inline-flex"
                >
                 <Tab {...tabStyle}>
                    <FiClock size={18} /> Manual Pass
                  </Tab>
                  <Tab {...tabStyle}>
                    <FiCpu size={18} /> AI Pass
                  </Tab>
                </TabList>
            </Flex>

            <TabPanels>
              <TabPanel p={0}>
                <CoolerPurtiyDp
                 fromDate={fromDate}
                 toDate={toDate}
                 variant="manualPass"/>
              </TabPanel>
              <TabPanel p={0}>
                <CoolerPurtiyDp
                 fromDate={fromDate}
                 toDate={toDate}
                 variant="aiFail"/>
              </TabPanel>
            </TabPanels>
            </Tabs>

          </TabPanel>
          
          
          
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default VarianceReportAiVsManual;